import { blue as daybreakBlue, cyan, purple } from '@ant-design/colors';
import {
  BusyTaskSourceTypes,
  CrrRankEnum,
  DecisionsTypes,
  EnumBusyEntityCachedDataSourceType,
  EnumTaskEntityCachedDataPaymentCreateOrigin,
  PaymentcreateOriginEnumType
} from '@tools/ui/types/generated/graphql';

const brandColor = {
  melio: 'rgb(120, 73, 255)',
  qb: 'rgb(46, 160, 29)'
};

export enum ColorModule {
  RECO = '#EB2F96',
  SHERLOCK = '#13C2C2',
  WATSON = '#FA8C16',
  SETTINGS = '#1890FF',
  BUSY = '#006699',
  RUBY = '#EFBC9B',
  GAYA = '#9befe0',
  COMM = '#199a26',
  DEFAULT = 'gray'
}

const gray = {
  gray0: 'rgba(0, 0, 0, 0)',
  gray45: 'rgba(0, 0, 0, 0.45)',
  gray65: 'rgba(0, 0, 0, 0.65)',
  gray85: 'rgba(0, 0, 0, 0.85)',
  gray70: '#8C8C8C',
  grayDark: '#595959',
  grayBlack: '#434343',
  gray40: '#f0f0f0',
  grayLight: '#d9d9d9',
  gray1: '#ffffff',
  gray2: '#fafafa',
  gray3: '#f5f5f5',
  gray9: '#262626',
  gray10: '#F9F0FF',
  gray6: '#BFBFBF',
  graySecondary: '#6C6C6C',
  gray4: '#666A79',
  gray5: '#E2E2E2',
  gray7: '#F9F9F9',
  gray11: '#808080',
  textSecondary: '#646f87',
  contactTag: '#E6F0FE'
};

const greenColors = { green1: '#F6FFED' };

const goldColors = { gold1: '#FFFBE6', warning: '#FFF7E6' };

export const success = 'rgb(46, 160, 29)';
export const failed = 'rgba(245, 34, 45, 1)';

const pending = 'rgb(219, 15, 148)';

export const blue = '#1890ff';
const green = '#52C41A';

export const theme = {
  spacing: (units = 1): number => units * 8,
  reco: {
    summaryCubes: {
      title: {
        check: purple.primary,
        ach: cyan.primary,
        card: daybreakBlue.primary,
        jpm: purple.primary,
        evolve: cyan.primary,
        svb: daybreakBlue.primary
      }
    }
  },
  sherlock: {
    escalations: {
      commentDrawer: {
        buttonColor: '#1677ff',
        headerBackground: '#F0F2F5',
        buttonMute: '#1890ff',
        buttonUnmute: '#d9d9d9',
        newComment: { dividerColor: '#D8D8D8' }
      }
    },
    ocrError: '#F5222D'
  },
  colors: {
    rangeBadge: (
      negativeValue: number,
      value: number,
      naturalValue?: number
    ): string => {
      if (value > negativeValue) return failed;
      if (naturalValue && value === naturalValue) {
        return gray.gray70;
      }
      return success;
    },
    booleanTag: {
      true: 'success',
      false: 'error',
      orange: 'orange'
    },
    iconLink: '#40A9FF',
    primary: 'rgb(120, 73, 255)',
    white: '#fff',
    link: blue,
    deletedEntities: 'red',
    blue: {
      blue1: '#E6F7FF',
      primary: blue,
      background: '#f0f2f5',
      light: '#E4EEFF',
      blue5: '#40A9FF',
      activeFilter: '#1677ff',
      geekblue1: '#D1E0FF',
      contactTag: '#003EB3'
    },
    red: 'red',
    lightRed: '#FFF1F0',
    orange: 'orange',
    green,
    primaryGreen: 'green',
    warning: 'orange',
    warningColor: 'rgba(232, 115, 9, 0.3)',
    alertColor: '#fff8db',
    completed: green,
    background: 'lightGray',
    layoutBackground: '#ffffff',
    lightBackground: '#f0f0f0',
    primaryLight: '#D9CBFF',
    queryBuilder: 'rgba(250, 240, 210, 0.5)',
    gray,
    greenColors,
    goldColors,
    black: 'black',
    semanticCritical: '#CF1322',
    brand: {
      melio: brandColor.melio,
      qb: brandColor.qb
    },
    filters: {
      tag: '#F3F3F3'
    },
    boolean: {
      yes: success,
      no: failed
    },
    isVerified: {
      yes: undefined,
      no: '#bfbfbf'
    },
    timeline: {
      green: 'green',
      red: 'red'
    },
    task: {
      status: {
        unread: 'rgb(222, 178, 4)',
        in_progress: 'rgb(78, 154, 209)',
        completed: success
      }
    },
    verificationTask: {
      sourceType: {
        [EnumBusyEntityCachedDataSourceType.IntuitVcBusinessSubmissionForm]:
          'green',
        [EnumBusyEntityCachedDataSourceType.JpmVcBusinessFileUpload]: 'cyan',
        [EnumBusyEntityCachedDataSourceType.MSN]: 'blue',
        [EnumBusyEntityCachedDataSourceType.MSNGetPaidOrganizationFileUpload]:
          'blue',
        [EnumBusyEntityCachedDataSourceType.StrategicSegmentsFile]: 'orange',
        [EnumBusyEntityCachedDataSourceType.UnlinkBusinessMatch]: 'magenta',
        [EnumBusyEntityCachedDataSourceType.VcBusinessSubmissionForm]: 'purple'
      },
      isRevived: '#8C8C8C',
      relatedTasksBackgroundColor: gray.gray40,
      relatedTasksFontColor: gray.gray9
    },
    unmatchTask: {
      reviewTask: {
        tagModified: '#FA8C16',
        tagActivatedBg: '#ff4446',
        card: {
          border: '#D9D9D9',
          rowLines: '#f0f0f0',
          header: gray.gray2
        },
        notAvailable: gray.gray6,
        tabContentBorder: '#F0F5FF',
        deletedContent: '#FF0000',
        deepLinks: '#2F54EB',
        businessInfoBackground: '#F0F2F5'
      }
    },
    payment: {
      createOrigin: {
        [PaymentcreateOriginEnumType.pay]: 'purple',
        [EnumTaskEntityCachedDataPaymentCreateOrigin.pay]: 'purple',
        [PaymentcreateOriginEnumType.qboIntegration]: 'green',
        [EnumTaskEntityCachedDataPaymentCreateOrigin.qbo]: 'green',
        [PaymentcreateOriginEnumType.qbrIntegration]: 'green',
        [EnumTaskEntityCachedDataPaymentCreateOrigin.qbr]: 'green',
        [PaymentcreateOriginEnumType.qbmIntegration]: 'green',
        [EnumTaskEntityCachedDataPaymentCreateOrigin.qbm]: 'green',
        [PaymentcreateOriginEnumType.qbrIos]: 'green',
        [PaymentcreateOriginEnumType.qbrAndroid]: 'green',
        [PaymentcreateOriginEnumType.qbdtIntegration]: 'green',
        [PaymentcreateOriginEnumType.qbdtMac]: 'green',
        [PaymentcreateOriginEnumType.qbdtWindows]: 'green',
        [EnumTaskEntityCachedDataPaymentCreateOrigin.qbdt]: 'green',
        [PaymentcreateOriginEnumType.qbmIos]: 'green',
        [PaymentcreateOriginEnumType.qbmAndroid]: 'green',
        [PaymentcreateOriginEnumType.request]: 'orange',
        [EnumTaskEntityCachedDataPaymentCreateOrigin.request]: 'orange',
        [EnumTaskEntityCachedDataPaymentCreateOrigin.built]: 'default',
        organizationOrigin: 'purple',
        paymentOrigin: 'default'
      },
      taskStatus: {
        unread: 'gray',
        read: 'blue',
        inProgress: 'yellow',
        pending: 'yellow',
        default: 'gray'
      },
      ekata: {
        ekataIcon: 'black',
        match: 'green',
        notFound: 'gray',
        noMatch: 'rgb(222, 178, 4)',
        default: 'gray'
      },
      status: {
        scheduled: 'rgb(222, 178, 4)',
        'in-progress': 'rgb(78, 154, 209)',
        completed: success,
        failed,
        blocked: pending
      }
    },
    organization: {
      sanctions: {
        yes: 'success',
        no: 'error'
      },
      kybStatus: {
        undecided: 'default',
        accepted: 'success',
        denied: 'error'
      },
      idmDecision: {
        yes: 'success',
        no: 'default'
      },
      kybSubType: {
        standard: 'success',
        sensitive: 'default',
        tentative: 'default',
        rest: 'error'
      },
      kybMoneyIn: {
        undecided: 'default',
        accept: 'success',
        reject: 'error',
        policy: 'error',
        approveAll: 'rgb(208, 78, 198)',
        legal: 'rgb(224, 82, 106)'
      },
      status: {
        [DecisionsTypes.BlockRisk]: '#FA8C16',
        [DecisionsTypes.BlockCompliance]: '#FA8C16',
        [DecisionsTypes.Reject]: '#CF1322',
        [DecisionsTypes.Accept]: '#389E0D',
        [DecisionsTypes.Block]: '#8C8C8C'
      },
      labelsStatus: {
        [DecisionsTypes.BlockRisk]: 'orange',
        [DecisionsTypes.BlockCompliance]: 'orange',
        [DecisionsTypes.Reject]: 'red',
        [DecisionsTypes.Accept]: 'green',
        [DecisionsTypes.Block]: 'orange'
      },
      mql: {
        undecided: 'default',
        qualified: 'success',
        unqualified: 'error'
      },
      isFRR: '#d46b08',
      crr: {
        rank: {
          [CrrRankEnum.Low]: 'green',
          [CrrRankEnum.Medium]: 'orange',
          [CrrRankEnum.High]: 'red'
        }
      }
    },
    emailAge: {
      emailAgeScore: (
        score: number
      ): { backgroundColor: string; color: string; cursor: string } => {
        const cursor = 'pointer';
        let backgroundColor = green;
        let color = '#fff';
        if (score === 500) {
          backgroundColor = '#F0F0F0';
          color = '#000';
        }
        if (score > 500) {
          backgroundColor = '#d46b08';
        }
        if (score > 700) {
          backgroundColor = '#FF4D4F';
        }

        return { backgroundColor, color, cursor };
      }
    },
    ribbon: {
      backgroundColor: 'rgb(59,219,15)',
      color: '#fff'
    },
    deliveryMethod: {
      tag: {
        new: '#1890ff',
        changed: '#FA8C16'
      }
    },
    value: {
      yes: success,
      no: failed,
      notAvailable: 'rgb(170, 170, 170)'
    },
    risk: {
      status: {
        approved: success,
        declined: failed,
        pending
      }
    },
    table: {
      summary: 'rgb(250, 250, 250, 1)',
      border: gray.gray40,
      groupColBackground: '#001529'
    },
    checkbox: {
      selected: { color: blue, backgroundColor: '#e6f7ff' },
      hover: '#f5f5f5'
    },
    drawer: {
      payor: {
        main: '#D6E4FF',
        sub: '#F0F5FF'
      },
      payee: {
        main: '#FFD8BF',
        sub: '#FFF2E8'
      },
      payment: {
        main: '#F0F2F5',
        sub: '#F0F2F5'
      },
      enrichment: {
        main: '#F0F2F5',
        sub: '#F0F2F5'
      }
    },
    watson: {
      container: {
        background: '#F0F2F5'
      }
    },
    shiftManager: {
      container: {
        main: '#F0F2F5'
      },
      topBox: {
        background: '#F0F2F5',
        title: '#8c8c8c',
        icon: '#1890ff'
      },
      statisticsCounterCard: {
        title: '#8c8c8c',
        background: '#FFFFFF'
      },
      assigning: {
        icon: '#1a90fe'
      },
      busy: {
        sourceType: {
          [BusyTaskSourceTypes.StrategicSegmentsFile]: '#FA8C16',
          [BusyTaskSourceTypes.Msn]: '#1890FF',
          [BusyTaskSourceTypes.MsnGetPaidOrganizationFileUpload]: '#1890FF',
          [BusyTaskSourceTypes.JpmVcBusinessFileUpload]: '#13C2C2',
          [BusyTaskSourceTypes.VcBusinessSubmissionForm]: '#9254DE',
          [BusyTaskSourceTypes.UnlinkBusinessMatch]: '#EB2F96',
          [BusyTaskSourceTypes.IntuitVcBusinessSubmissionForm]: '#9254DE'
        }
      }
    },
    comments: {
      true: { backgroundColor: '#E6F7FF', color: blue },
      false: { backgroundColor: '#F0F0F0', color: 'rgba(0, 0, 0, 0.45)' }
    },
    sideContainer: {
      background: '#001529',
      backgroundActive: '#1a2c3d',
      icon: '#D9D9D9'
    },
    tab: {
      active: '#e6f7ff',
      text: '#262626'
    },
    info: {
      warning: '#FAAD14'
    },
    accountReview: {
      background: '#F0F2F5',
      users: {
        contactAvatar: '#722ED1'
      },
      noTableData: '#595959',
      statistics: {
        zendesk: { count: '#1785FF' }
      }
    },
    verificationGroupTasks: {
      similarBusinesses: {
        background: '#F0F2F5'
      },
      background: '#FFFFFF'
    }
  }
};
